import { useState } from 'react';
import { useIntl } from 'react-intl';
import { getFeesBySolutionTotalPrice, useFeesBySolutionContext } from 'sonata-platform-components';
import { Checkbox, Input } from 'sonata-react-components';
import { normalizeWithUnit } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import './totalInvoice.scss';

import type { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';
import type { FeeSolution } from 'sonata-static-storage/lib/schemas/feeSolution/feeSolution.schema';

type Props = {
  currencyInfo: BaseCurrencyInterface;
  defaultMaxCost?: number;
  onChange: (value: number | null) => void;
  selectedSolution?: FeeSolution;
};

type State = {
  isMaxCostEnabled: boolean;
  maxCost: number;
};

export default function TotalInvoice({
  currencyInfo,
  defaultMaxCost,
  onChange,
  selectedSolution
}: Readonly<Props>): JSX.Element {
  const [{ isMaxCostEnabled, maxCost }, setState] = useState<State>({
    isMaxCostEnabled: defaultMaxCost !== undefined,
    maxCost: defaultMaxCost ?? 0
  });
  const { formatMessage } = useIntl();

  const { capabilities, capabilitiesByUser, capabilityCategories, getStep3, plans, user, userRoles } =
    useFeesBySolutionContext();
  const { selectedPlan } = getStep3();

  const numberOfDecimals = 4;

  const priceFormatter = (price: number): string => {
    return normalizeWithUnit(price, currencyInfo, 2).replace(
      currencyInfo.currencySymbol,
      ` ${currencyInfo.currencySymbol}`
    );
  };

  const handleChange = (key: keyof State, value: any) => {
    setState(prev => ({
      ...prev,
      [key]: value
    }));
    const emitMaxCost = key === 'isMaxCostEnabled' ? value : isMaxCostEnabled;
    const costToEmit = key === 'maxCost' ? value : maxCost;
    onChange(emitMaxCost ? Number(costToEmit) : null);
  };

  const plan = plans?.find((item: Record<string, any>) => item._id === selectedPlan?.plan);

  const hasCustomCostPermission =
    user && userRoles && checkUserResourcePermissions('feesBySolutionCustomCost', user, userRoles);

  const inputHasError = isMaxCostEnabled && isNaN(maxCost);

  const capabilitiesFilterByPlan = capabilities?.filter(({ _id }: Record<string, any>) =>
    plan!.extraCapabilities.includes(_id!)
  );
  const capabilitiesByUserFilterByPlan = capabilitiesByUser?.filter(
    ({ capability, feeSolution }: Record<string, any>) =>
      plan!.extraCapabilities.includes(capability) && feeSolution === plan!.feeSolution
  );
  const totalPrice = getFeesBySolutionTotalPrice({
    capabilities: capabilitiesFilterByPlan,
    capabilitiesByUser: capabilitiesByUserFilterByPlan,
    capabilityCategories,
    extraCapabilities: [],
    selectedPlan,
    selectedSolution
  });
  const formattedCampaignPrice = priceFormatter(totalPrice);
  const formattedTotalCost = priceFormatter(isMaxCostEnabled ? maxCost || 0 : totalPrice);
  const latestPrice = isMaxCostEnabled ? formattedTotalCost : formattedCampaignPrice;

  const solutionLabel = formatMessage({
    defaultMessage: 'Solution',
    id: 'feesBySolution.stepThree.invoice.solutionLabel'
  });
  const planLabel = formatMessage({
    defaultMessage: 'Planning type',
    id: 'feesBySolution.stepThree.invoice.planningTypeLabel'
  });

  const solutionName = selectedSolution?.copy ?? '';
  const solutionDescription = selectedSolution?.description ?? '';
  const planName = plan?.copy ?? '';
  const planDescription = plan?.description ?? '';

  return (
    <div className={`totalInvoice ${isMaxCostEnabled ? 'totalInvoice--maxCostEnabled' : ''}`}>
      <p className="totalInvoice__title">
        {formatMessage({
          defaultMessage: 'Invoice detail',
          id: 'feesBySolution.stepThree.invoice.detail'
        })}
      </p>
      <div className="totalInvoice__card" id="fees-totalInvoice-box">
        <div className="totalInvoice__body">
          {(solutionName || solutionDescription) && (
            <div className="totalInvoice__row">
              <span aria-hidden="true" className="totalInvoice__rowIcon">
                <i className="fa-solid fa-circle-check" />
              </span>
              <div className="totalInvoice__rowText">
                <span className="totalInvoice__rowLabel">{solutionLabel}</span>
                <div className="totalInvoice__rowDetail">
                  {solutionName && <span className="totalInvoice__rowValue">{solutionName}</span>}
                  {solutionDescription && (
                    <span className="totalInvoice__rowDescription">{`- ${solutionDescription}`}</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {(planName || planDescription) && (
            <div className="totalInvoice__row">
              <span aria-hidden="true" className="totalInvoice__rowIcon">
                <i className="fa-solid fa-circle-check" />
              </span>
              <div className="totalInvoice__rowText">
                <span className="totalInvoice__rowLabel">{planLabel}</span>
                <div className="totalInvoice__rowDetail">
                  {planName && <span className="totalInvoice__rowValue">{planName}</span>}
                  {planDescription && <span className="totalInvoice__rowDescription">{`- ${planDescription}`}</span>}
                </div>
              </div>
              <span className="totalInvoice__rowPrice">{`+ ${formattedCampaignPrice}`}</span>
            </div>
          )}

          {hasCustomCostPermission && (
            <>
              <div className="totalInvoice__divider" />
              <div className="totalInvoice__setCost">
                <div className="totalInvoice__setCostRow">
                  <div className="totalInvoice__setCostLeft">
                    <div className="totalInvoice__setCostSwitchGroup">
                      <Checkbox
                        active={isMaxCostEnabled}
                        id="fees-totalInvoice-switch"
                        onClickAction={() => handleChange('isMaxCostEnabled', !isMaxCostEnabled)}
                        type="switch"
                      />
                      <span className="totalInvoice__setCostLabel">
                        {formatMessage({
                          defaultMessage: 'Set Cost',
                          id: 'feesBySolution.stepThree.invoice.maximumCost'
                        })}
                      </span>
                    </div>
                    <span className="totalInvoice__setCostDescription">
                      {`- ${formatMessage({
                        defaultMessage: 'Configure a custom cost exclusively for this campaign.',
                        id: 'feesBySolution.stepThree.invoice.maximumCostMessage'
                      })}`}
                    </span>
                  </div>
                  {isMaxCostEnabled && (
                    <div className="totalInvoice__setCostInputWrapper">
                      <Input
                        className="totalInvoice__setCostInput"
                        currencyConfig={currencyInfo}
                        id="fees-totalInvoice-amount"
                        inputAdditionalProps={{
                          allowEmpty: true,
                          min: 0
                        }}
                        inputStatus={inputHasError ? 'wrong' : 'default'}
                        name="feesTotalInvoiceAmount"
                        notFloatingPlaceholder
                        numberFormat="currency"
                        numberOfDecimals={numberOfDecimals}
                        onChange={value => handleChange('maxCost', value)}
                        required
                        shape="square"
                        tiny
                        type="text"
                        value={maxCost}
                      />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        <div className="totalInvoice__footer">
          <span className="totalInvoice__footerLabel">
            {formatMessage({
              defaultMessage: 'Total to be paid',
              id: 'feesBySolution.stepThree.invoice.paid'
            })}
          </span>
          <div className="totalInvoice__footerPrices">
            <span className="totalInvoice__footerPriceLatest">{latestPrice}</span>
            {isMaxCostEnabled && <span className="totalInvoice__footerPriceInitial">{formattedCampaignPrice}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
